<script setup lang="ts">
  import { countryFormats } from '~/helpers/countryFormats'
  import { useWarehouseMan } from '~/stores/admin/warehouseMan/useWarehouseMan'
  import { warehousemanFields } from '../helpers/wareHousemanFields'
  import { userFields } from '../helpers/userFields'
  import { useWareHouseMan } from '../composables/useWareHouseMan'
  import { useTableDataStore } from '#imports'

  const emit = defineEmits<{
    (e: 'loading' | 'changed' | 'editing', value: boolean): void
  }>()

  const props = defineProps<{
    id: string
    isEditing: boolean
  }>()

  const { formChanged, formData, errors, resetForm, handleSubmit } =
    useWareHouseMan(props.id)

  const store = useTableDataStore('warehouseman')

  const { t } = useI18n()
  const toast = useToast()
  const { createWarehouseman, updateWarehouseman, getWarehouseman } =
    useWarehouseMan()

  watch(formChanged, (value) => {
    emit('changed', value)
  })

  onMounted(async () => {
    if (!props.id) emit('editing', true)
    if (props.id) {
      const response = await getWarehouseman(props.id)
      if (response) {
        formData.warehouseman = {
          full_name: response.full_name || '',
          short_name: response.short_name || '',
          ceo_name: response.ceo_name || '',
          ceo_position: response.ceo_position || '',
          founded_on_basis: response.founded_on_basis || '',
          inn: response.inn || '',
          kpp: response.kpp || '',
          ogrn: response.ogrn || '',
          ogrn_date: response.ogrn_date || '',
          tax_authority: response.tax_authority || '',
          bank_bik: response.bank_bik || '',
          bank_name: response.bank_name || '',
          bank_corr_account: response.bank_corr_account || '',
          bank_account: response.bank_account || '',
          legal_address: response.legal_address || '',
          contact_person_name: response.contact_person_name || '',
          contact_person_phone: response.contact_person_phone || null,
        }
        resetForm()
      }
    }
  })

  const showOperationToast = (
    success: boolean,
    action: 'create' | 'update'
  ) => {
    const type = success ? 'success' : 'error'
    toast.add({
      description: t(`forms.warehouseman.toast.${action}.${type}`),
      color: type === 'success' ? 'primary' : 'error',
    })
  }

  const onSave = handleSubmit(async () => {
    emit('loading', true)
    try {
      const action = props.id ? 'update' : 'create'
      const operation = props.id
        ? () => store.update(formData.warehouseman, props.id)
        : () => store.create(formData)

      const response = await operation()

      if (response) {
        emit('changed', false)
      }

      showOperationToast(Boolean(response), action)
    } finally {
      emit('loading', false)
    }
  })

  defineExpose({
    onSave,
  })
</script>

<template>
  <div
    class="flex !w-[1020px] flex-col gap-6 p-5 pt-0 max-[1060px]:!w-[800px] max-[850px]:!w-full max-md:p-0"
  >
    <h1 class="text-2xl font-bold">
      {{
        !id
          ? $t('forms.warehouseman.new_account')
          : $t('forms.warehouseman.edit_account')
      }}
    </h1>

    <TransitionContainer>
      <WareHousemanView
        v-if="!isEditing"
        :is-editing="isEditing"
        :warehouseman-fields="warehousemanFields"
        :warehouseman="formData.warehouseman"
      />

      <WareHousemanForm
        v-else
        :id="id"
        v-model="formData"
        :warehouseman-fields="warehousemanFields"
        :user-fields="userFields"
        :country-formats="countryFormats"
        :errors="errors"
      />
    </TransitionContainer>
  </div>
</template>
